// Detects whether the device is touch-capable. Drives the product drag
// affordance: on touch devices a dedicated drag handle is shown (so the rest of
// the row is free to scroll under the finger); on non-touch devices the whole
// row is the drag source and no handle is shown. This is independent of the
// layout breakpoint — a touch tablet on the desktop layout still gets the
// handle, and a non-touch narrow window on the mobile layout still drags the
// whole row.
//
// Uses the `(pointer: coarse)` media query (the primary pointer is touch-like)
// with a `maxTouchPoints` fallback. We treat "has a coarse pointer" as touch —
// hybrid devices (touch laptops) report coarse when touch is the active modality
// in many browsers; pointer:coarse is the most reliable signal short of watching
// live pointer events. SSR-safe (defaults false, corrects on mount).

import { useEffect, useState } from "react";

function detectTouch(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches) {
      return true;
    }
  } catch {
    /* matchMedia unavailable — fall through */
  }
  if (typeof navigator !== "undefined" && typeof navigator.maxTouchPoints === "number") {
    return navigator.maxTouchPoints > 0;
  }
  return "ontouchstart" in (typeof window !== "undefined" ? window : ({} as Window));
}

export function useIsTouch(): boolean {
  const [isTouch, setIsTouch] = useState<boolean>(detectTouch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia("(pointer: coarse)");
    const onChange = () => setIsTouch(detectTouch());
    // Re-evaluate once on mount + whenever the coarse-pointer state flips (e.g.
    // a 2-in-1 docking/undocking).
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isTouch;
}
